import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Button, cn, Dialog, Field, Input, Textarea } from '@/core/ui';
import { useCancelVisit, useScheduleVisit } from '../application/queries';
import {
  cancelSchema,
  MODALITIES,
  scheduleSchema,
  splitLocal,
  toScheduleBody,
  VisitStatus,
  type CancelForm,
  type ScheduleForm,
  type Visit,
} from '../domain/visit';
import { actionError } from './errors';

const today = () => splitLocal(new Date().toISOString()).date;

/** Agendar — ou reagendar, já preenchido com o que estava marcado. */
export function ScheduleDialog({
  visit,
  open,
  onClose,
}: {
  visit: Visit;
  open: boolean;
  onClose: () => void;
}) {
  const schedule = useScheduleVisit();
  const rescheduling = visit.status === VisitStatus.SCHEDULED;
  const form = useForm<ScheduleForm>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: initial(visit),
  });
  const modality = useWatch({ control: form.control, name: 'modality' });
  const { errors } = form.formState;

  // Abriu de novo (ou para outro pedido): começa do que está salvo.
  useEffect(() => {
    if (open) {
      form.reset(initial(visit));
      schedule.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir ou trocar de pedido
  }, [open, visit.id]);

  const submit = form.handleSubmit((values) =>
    schedule.mutate({ id: visit.id, body: toScheduleBody(values) }, { onSuccess: onClose }),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={rescheduling ? 'Reagendar visita' : 'Agendar visita'}
      description={visit.requester_name}
      className="max-w-lg"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dia" error={errors.date?.message}>
            {(p) => <Input {...p} type="date" min={today()} {...form.register('date')} />}
          </Field>
          <Field label="Horário" error={errors.time?.message}>
            {(p) => <Input {...p} type="time" step={900} {...form.register('time')} />}
          </Field>
        </div>
        <Field label="Analista" error={errors.analyst_name?.message}>
          {(p) => (
            <Input
              {...p}
              autoComplete="off"
              placeholder="Nome de quem vai visitar"
              {...form.register('analyst_name')}
            />
          )}
        </Field>
        <fieldset>
          <legend className="mb-1.5 block text-xs font-medium text-fg-subtle">Modalidade</legend>
          <div className="flex gap-2">
            {MODALITIES.map((m) => (
              <label
                key={m.value}
                className={cn(
                  'flex flex-1 cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-sm transition-colors has-focus-visible:ring-2 has-focus-visible:ring-teal-300',
                  modality === m.value
                    ? 'border-brand-400 bg-brand-400/10 font-medium text-fg'
                    : 'border-line-strong text-fg-subtle hover:bg-surface-2',
                )}
              >
                <input type="radio" value={m.value} className="sr-only" {...form.register('modality')} />
                {m.label}
              </label>
            ))}
          </div>
        </fieldset>
        <Field
          label={modality === 'online' ? 'Link da reunião (opcional)' : 'Endereço (opcional)'}
          hint="A pessoa vê no portal."
          error={errors.location?.message}
        >
          {(p) => (
            <Input
              {...p}
              placeholder={modality === 'online' ? 'https://meet…' : 'Rua, número — cidade'}
              {...form.register('location')}
            />
          )}
        </Field>
        <Field label="Nota do time (opcional)" hint="Só o time vê." error={errors.team_note?.message}>
          {(p) => <Textarea {...p} rows={3} {...form.register('team_note')} />}
        </Field>

        {schedule.isError && (
          <p role="alert" className="text-sm text-fg">
            {actionError(schedule.error)}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" loading={schedule.isPending}>
            {rescheduling ? 'Salvar novo horário' : 'Agendar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function initial(visit: Visit): ScheduleForm {
  const { date, time } = splitLocal(visit.scheduled_for);
  return {
    date,
    time,
    analyst_name: visit.analyst_name ?? '',
    modality: visit.modality === 'online' ? 'online' : 'presencial',
    location: visit.location,
    team_note: visit.team_note,
  };
}

/** Cancelar pelo time: o motivo é obrigatório e fica só no backoffice. */
export function CancelDialog({ visit, open, onClose }: { visit: Visit; open: boolean; onClose: () => void }) {
  const cancel = useCancelVisit();
  const form = useForm<CancelForm>({ resolver: zodResolver(cancelSchema), defaultValues: { reason: '' } });

  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      cancel.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir ou trocar de pedido
  }, [open, visit.id]);

  const submit = form.handleSubmit(({ reason }) =>
    cancel.mutate({ id: visit.id, reason: reason.trim() }, { onSuccess: onClose }),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Cancelar o pedido de visita?"
      description={visit.requester_name}
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <p className="text-sm text-fg-muted">
          No portal, a pessoa vê “Cancelado pelo nosso time” e pode pedir de novo. Avise-a pelo WhatsApp.
        </p>
        <Field
          label="Motivo"
          hint="Fica registrado só no backoffice."
          error={form.formState.errors.reason?.message}
        >
          {(p) => <Textarea {...p} rows={3} {...form.register('reason')} />}
        </Field>
        {cancel.isError && (
          <p role="alert" className="text-sm text-fg">
            {actionError(cancel.error)}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Manter pedido
          </Button>
          <Button type="submit" variant="danger" loading={cancel.isPending}>
            Cancelar pedido
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
